import { DeleteForever } from '@mui/icons-material';
import { Button, FormHelperText, IconButton, Paper } from '@mui/material';
import { ValidationError } from 'class-validator';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { StoreService } from '~/service/service.store';
import { FragranceCreateDto, FragranceOfferDto } from '~/shared/dto/admin/fragrance.dto';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldNumber } from '~/view/fields/field.number';
import { FieldSelect } from '~/view/fields/field.select';
import { FieldSwitch } from '~/view/fields/field.switch';
import { FieldText } from '~/view/fields/field.text';
import { Col, Row } from '~/view/view.box';

type FragranceOffersProps = {
	field: Form.Field<FragranceOfferDto[]>;
	errors: ValidationError[] | undefined;
};
export const FragranceOffers = observer(({ field, errors }: FragranceOffersProps) => {
	const [store] = useService(StoreService);
	store.useNames();

	const handleAdd = useCallback(() => {
		field.set([...field.value, { available: true, id: -Date.now() } as FragranceOfferDto]);
	}, [field]);

	const handleRemove = useCallback((id: number) => field.set(field.value.filter(o => o.id !== id).slice()), [field]);

	const handleChange = useCallback(
		(id: number, dto: FragranceOfferDto) => {
			const newOffers = [...field.value];
			const index = newOffers.findIndex(o => o.id === id);
			if (index !== -1) {
				newOffers[index] = dto;
				field.set(newOffers);
			}
		},
		[field],
	);

	const error = useMemo(
		() => errors?.find(e => e.target instanceof FragranceCreateDto && e.property === 'offers'),
		[errors],
	);

	return (
		<Col>
			<Col gap={2} mb={2}>
				{field.value.map(offer => (
					<FragranceOffer
						key={offer.id}
						offer={offer}
						onRemove={() => handleRemove(offer.id!)}
						onChange={dto => handleChange(offer.id!, dto)}
					/>
				))}
			</Col>
			<FormHelperText sx={{ mb: 2 }} error={!!error}>
				{Object.values(error?.constraints || {}).join('\n')}
			</FormHelperText>
			<Button variant="outlined" onClick={handleAdd}>
				Add Offer
			</Button>
		</Col>
	);
});

// TODO: fetch currencies from backend
const currencyItems = [{ key: 'USD' }, { key: 'EUR' }, { key: 'GBP' }];

type FragranceOfferProps = {
	offer: FragranceOfferDto;
	onRemove: () => void;
	onChange: (dto: FragranceOfferDto) => void;
};

const FragranceOffer = observer(({ offer, onRemove, onChange }: FragranceOfferProps) => {
	const [store] = useService(StoreService);

	const form = Form.use(FragranceOfferDto, {
		name: 'FragranceOfferDto',
		initial: offer,
		onChange: dto => setTimeout(() => onChange(dto), 0),
		validateOn: 'submit',
	});

	return (
		<Paper elevation={3} sx={{ p: 2 }}>
			<Col gap={2}>
				<Row gap={2} alignItems="flex-start" justifyContent="space-between">
					<FieldSelect
						items={store.names}
						field={form.$storeId}
						valueProp="id"
						labelProp="name"
						enabledProp="active"
					/>
					<FieldText field={form.$title} />
				</Row>
				<Row gap={2} alignItems="flex-start" justifyContent="space-between">
					<FieldSelect field={form.$currency} items={currencyItems} labelProp="key" valueProp="key" />
					<FieldNumber field={form.$price} />
					<FieldNumber field={form.$discountPrice} />
				</Row>
				<FieldText field={form.$buyUrl} />
				<Row gap={2} alignItems="flex-start" justifyContent="space-between">
					<FieldSwitch field={form.$available} />
					<IconButton onClick={onRemove}>
						<DeleteForever />
					</IconButton>
				</Row>
			</Col>
		</Paper>
	);
});
